/**
 * Step 7.4 -- parse each file once, however many characters are on screen.
 *
 * Eight characters wearing the same twelve slots and drawing on the same clip bundles
 * were, until this existed, eight independent parses of the same twenty files. The
 * browser caches the *bytes*; nothing caches the work of turning them into meshes,
 * skeletons and animation curves, and that work is what the measurement showed: the
 * eighth character cost as much as the first.
 *
 * Two things are cached, and they are cached differently because they are used
 * differently:
 *
 * - **Asset containers**, per scene. A container is the parsed file. Characters never
 *   take its meshes -- they take copies (`instantiateModelsToScene`) or rebind its
 *   animation curves onto their own skeleton -- so it can be kept and reused. It is
 *   keyed by scene because the objects inside belong to one engine.
 * - **Tint palettes**, globally. A palette is the decoded colourway plus the pixel
 *   index of every tintable region: pure data, no engine objects, and the expensive
 *   part (a full scan of a megapixel mask per region) does not vary by character.
 *
 * What is *not* cached is anything a character owns: its skeleton, its meshes, its
 * animation groups, its baked atlas. Those differ per character by definition, and
 * sharing them would make two characters move as one.
 */
import { LoadAssetContainerAsync } from '@babylonjs/core/Loading/sceneLoader';
import type { AssetContainer } from '@babylonjs/core/assetContainer';
import type { Scene } from '@babylonjs/core/scene';

import { loadTintPalette, type TintPalette, type TintRegionSource } from './heroTintMaterial.ts';

import '@babylonjs/loaders/glTF/2.0';

interface Entry {
  container: Promise<AssetContainer>;
  /** Pinned entries are never forgotten -- see `SharedContainerOptions.hold`. */
  pinned: boolean;
}

interface SceneCache {
  /** Insertion-ordered, and re-inserted on every hit, so the head is the coldest. */
  containers: Map<string, Entry>;
}

/**
 * How many evictable files one scene keeps parsed.
 *
 * A pack's twelve slots and three weapons fit comfortably; the number exists for the
 * editor, where a builder browsing 720 parts would otherwise accumulate every one they
 * ever clicked.
 */
export const SHARED_CONTAINER_LIMIT = 48;

export interface SharedContainerOptions {
  /**
   * `copy` -- the caller instantiates copies and keeps nothing from the container, so
   * forgetting the parse costs only a re-parse. Parts and weapons.
   *
   * `pin` -- the caller hands out objects from *inside* the container: a clip bundle's
   * animation curves are referenced rather than copied, which is what lets one parse
   * drive every character. Forgetting one would still be correct -- the holder keeps
   * its promise -- but the next character would re-parse and end up with its own copy
   * of curves that were supposed to be shared, silently undoing the whole point. There
   * are a handful of bundles per pack, so pinning them is bounded by the pack format
   * rather than by how much a builder browses.
   */
  hold?: 'copy' | 'pin';
}

const scenes = new WeakMap<Scene, SceneCache>();
const palettes = new Map<string, Promise<TintPalette>>();

/** Counters the perf harness reads; also the cheapest way to prove sharing happened. */
export const assetCacheStats = {
  containerHits: 0,
  containerMisses: 0,
  containerEvictions: 0,
  paletteHits: 0,
  paletteMisses: 0,
};

function cacheFor(scene: Scene): SceneCache {
  let cache = scenes.get(scene);
  if (!cache) {
    cache = { containers: new Map() };
    scenes.set(scene, cache);
    // A disposed scene must not leave its parsed files reachable.
    scene.onDisposeObservable.addOnce(() => {
      for (const entry of cache?.containers.values() ?? []) {
        void entry.container.then((container) => container.dispose()).catch(() => {});
      }
      scenes.delete(scene);
    });
  }
  return cache;
}

/**
 * The parsed file behind `url`, loaded at most once per scene.
 *
 * The returned container is shared: callers must copy or rebind what they need from
 * it and must never dispose it, add its meshes to the scene, or mutate its animation
 * curves in place.
 */
export function loadSharedContainer(
  scene: Scene,
  url: string,
  options: SharedContainerOptions = {},
): Promise<AssetContainer> {
  const cache = cacheFor(scene);
  const existing = cache.containers.get(url);
  if (existing) {
    assetCacheStats.containerHits += 1;
    if (options.hold === 'pin') existing.pinned = true;
    // Re-insert so the most recently used entry is the last to be evicted.
    cache.containers.delete(url);
    cache.containers.set(url, existing);
    return existing.container;
  }
  assetCacheStats.containerMisses += 1;

  const container = LoadAssetContainerAsync(url, scene).catch((error: unknown) => {
    // A failed parse must not be remembered as the answer for every later character.
    cache.containers.delete(url);
    throw error;
  });
  cache.containers.set(url, { container, pinned: options.hold === 'pin' });
  evict(cache);
  return container;
}

/**
 * Drops the coldest evictable parses until the cache is back within its limit.
 *
 * Eviction **forgets, it does not dispose**, and that is deliberate. A caller holds
 * the promise it was handed across an `await`, so an entry can be evicted between the
 * call and the use -- assembling a character asks for twelve slots at once, and with a
 * small limit the first were being disposed while the last were still loading. The
 * symptom was "no skeleton in the part GLB" on a file that had loaded perfectly.
 *
 * Forgetting has neither hazard: whoever still holds the promise keeps a working
 * container, a later request re-parses, and the garbage collector reclaims the rest.
 * These containers are never added to the scene and never rendered, so there are no
 * GPU resources that only an explicit `dispose` would release.
 */
function evict(cache: SceneCache): void {
  const evictable = [...cache.containers].filter(([, entry]) => !entry.pinned);
  let excess = evictable.length - SHARED_CONTAINER_LIMIT;
  for (const [url] of evictable) {
    if (excess <= 0) break;
    cache.containers.delete(url);
    assetCacheStats.containerEvictions += 1;
    excess -= 1;
  }
}

/**
 * A decoded colourway with its regions resolved, loaded at most once per combination.
 *
 * Keyed by the colourway *and* the region list, since a pack that enables a different
 * set of tint regions produces a different palette from the same atlas.
 */
export function loadSharedTintPalette(
  colourwayUrl: string,
  regions: TintRegionSource[],
): Promise<TintPalette> {
  const key = `${colourwayUrl}|${regions.map((region) => `${region.id}@${region.mask}:${region.channel}`).join(',')}`;
  const existing = palettes.get(key);
  if (existing) {
    assetCacheStats.paletteHits += 1;
    return existing;
  }
  assetCacheStats.paletteMisses += 1;

  const pending = loadTintPalette(colourwayUrl, regions).catch((error: unknown) => {
    palettes.delete(key);
    throw error;
  });
  palettes.set(key, pending);
  return pending;
}

/** Drops every cached palette. Containers go with their scene. */
export function clearSharedPalettes(): void {
  palettes.clear();
}
